import React, { useEffect, useRef, useState } from 'react';
import { ScanResult, Responses, Respondent } from '../types';
import { TIER_MESSAGES, SCHEDULING_LINK, SECTION_TIPS, SECTIONS, AUTOMATION_EMAIL_URL, sanitizePayload } from '../constants';
import ChatUnlock from './ChatUnlock';

interface ResultsProps {
  responses: Responses;
  onReset: () => void;
  onSubmit: (respondent: Respondent) => Promise<void>;
  isSubmitting: boolean;
  isSuccess: boolean;
  calculateResults: (respondent: Respondent | null) => ScanResult | null;
}

const Results: React.FC<ResultsProps> = ({ responses, onReset, onSubmit, isSubmitting, isSuccess, calculateResults }) => {
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [showSheet, setShowSheet] = useState(true);
  const [isEmailing, setIsEmailing] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [finalRespondent, setFinalRespondent] = useState<Respondent | null>(() => {
    const saved = localStorage.getItem('ddg_scan_profile');
    return saved ? JSON.parse(saved) : null;
  });
  
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<any>(null);
  const resultsHeaderRef = useRef<HTMLHeadingElement>(null);

  const previewData = calculateResults(finalRespondent || {
    name: 'Leader', email: '', company: 'Fintech Corp', position: 'Strategy', sector: '', consent: false
  });

  // Ensure scroll top when landing on results
  useEffect(() => {
    window.parent.postMessage({ type: 'scrollToTop' }, '*');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (chartRef.current && previewData) {
      const labels = ["Trade Shows", "Customer Events", "Partner Events", "Executive Events", "Measurement"];
      const scores = [
        previewData.sectionScores.A, previewData.sectionScores.B, previewData.sectionScores.C,
        previewData.sectionScores.D, previewData.sectionScores.E,
      ];

      if (chartInstance.current) chartInstance.current.destroy();

      // @ts-ignore
      chartInstance.current = new window.Chart(chartRef.current, {
        type: 'radar',
        data: {
          labels,
          datasets: [{
            label: 'Maturity Score',
            data: scores,
            backgroundColor: 'rgba(130, 109, 236, 0.25)',
            borderColor: '#826dec',
            pointBackgroundColor: '#826dec',
            pointBorderColor: '#fff',
            pointHoverBackgroundColor: '#fff',
            pointHoverBorderColor: '#826dec',
            borderWidth: 4,
            fill: true,
          }]
        },
        options: {
          scales: {
            r: {
              beginAtZero: true, min: 0, max: 3,
              ticks: { stepSize: 1, display: false },
              grid: { color: 'rgba(255, 255, 255, 0.05)' },
              angleLines: { color: 'rgba(255, 255, 255, 0.05)' },
              pointLabels: {
                font: { size: 10, weight: '900' },
                color: 'rgba(255, 255, 255, 0.5)'
              }
            }
          },
          plugins: { legend: { display: false } },
          maintainAspectRatio: false
        }
      });
    }
  }, [previewData, isUnlocked]);

  const handleUnlock = async (data: Respondent) => {
    setFinalRespondent(data);
    await onSubmit(data);
    setIsUnlocked(true);
    setShowSheet(false);
    window.parent.postMessage({ type: 'scrollToTop' }, '*');
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => resultsHeaderRef.current?.focus(), 100);
  };

  const handleEmailResults = async () => {
    if (!finalRespondent || !previewData) return;
    setIsEmailing(true);
    try {
      const payload = sanitizePayload({ ...previewData, sendResultsEmail: true });
      await fetch(AUTOMATION_EMAIL_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      setEmailSent(true);
    } catch (err) { setEmailSent(true); } 
    finally { setIsEmailing(false); }
  };

  if (!previewData) return null;

  return (
    <div className="relative">
      {/* 
          LOCKED STATE:
          - max-h-[700px] provides a large defined container.
          - Aggressive blur hides data while revealing the chart shape.
      */}
      <div className={`p-8 md:p-12 transition-all duration-1000 ${!isUnlocked ? 'is-blurred max-h-[700px] overflow-hidden mb-12 opacity-40' : 'animate-fade-in'}`}>
        <div id="results-content">
          <div className="flex justify-between items-start mb-6">
            <div>
              <span className="text-[10px] font-black text-primary uppercase tracking-[0.3em]">Analysis Complete</span>
              <h2 ref={resultsHeaderRef} tabIndex={-1} className="text-3xl font-[900] text-white mt-2 uppercase italic tracking-tight">{previewData.tier} Tier</h2>
            </div>
            <div className="text-right">
              <div className="text-4xl font-black text-primary drop-shadow-[0_0_15px_rgba(130,109,236,0.4)]">{previewData.totalScore}<span className="text-sm text-gray-600 font-black">/45</span></div>
            </div>
          </div>

          <div className="bg-white/[0.03] border border-white/5 p-5 rounded-2xl mb-6 backdrop-blur-md">
            <p className="text-md font-bold text-gray-200 leading-snug italic">
              "{TIER_MESSAGES[previewData.tier]}"
            </p>
          </div>

          <div className="mb-6 chart-container">
            <canvas ref={chartRef}></canvas>
          </div>

          <div className="space-y-12 mb-16">
            <h3 className="text-sm font-black text-gray-500 uppercase tracking-[0.4em] border-b border-white/5 pb-4">Strategy Breakdown</h3>
            <div className="grid grid-cols-1 gap-10">
              {SECTIONS.map(sec => {
                const score = previewData.sectionScores[sec.id];
                return (
                  <div key={sec.id} className="flex gap-6 group">
                    <div className="text-3xl font-black min-w-[3.5rem] text-primary group-hover:scale-110 transition-transform duration-300">
                      {score.toFixed(1)}
                    </div>
                    <div>
                      <h4 className="font-black text-white uppercase text-sm tracking-widest mb-2">{sec.title}</h4>
                      <p className="text-sm text-gray-400 font-medium leading-relaxed">{SECTION_TIPS[sec.id]}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="bg-premium border border-white/10 p-10 rounded-3xl mb-12 relative overflow-hidden shadow-2xl">
            <div className="relative z-10">
              <h3 className="text-2xl font-black text-white uppercase italic tracking-tight mb-4">Execute Your Roadmap</h3>
              <p className="text-gray-400 mb-8 leading-relaxed font-medium max-w-sm">
                Transform these benchmarks into a data-driven strategy. Schedule a call with our Event Specialist to discuss your personalised potential.
              </p>
              <a 
                href={SCHEDULING_LINK} 
                target="_blank" 
                className="inline-block bg-primary text-white font-[900] py-4 px-10 rounded-full shadow-lg hover:bg-opacity-90 transition-all transform hover:scale-105 uppercase text-sm tracking-widest"
              >
                Schedule Call
              </a>
            </div>
            <div className="absolute top-0 right-0 w-48 h-48 bg-primary/10 rounded-full blur-[80px] -mr-24 -mt-24"></div>
          </div>
        </div>

        {isUnlocked && (
          <div className="flex flex-col gap-6">
            {emailSent ? (
              <div className="bg-green-500/10 border border-green-500/20 text-green-400 p-6 rounded-2xl text-center font-bold tracking-wide animate-fade-in shadow-lg">
                REPORT TRANSMITTED SUCCESSFULLY
              </div>
            ) : (
              <button
                onClick={handleEmailResults}
                disabled={isEmailing}
                className="w-full bg-primary hover:bg-opacity-90 text-white font-black py-5 rounded-2xl transition-all flex items-center justify-center uppercase text-sm tracking-widest glow-btn disabled:opacity-50"
              >
                {isEmailing ? 'Transmitting Data...' : 'Email Full Report'}
              </button>
            )}
            <button onClick={onReset} className="text-gray-600 hover:text-primary transition-colors text-[10px] font-black uppercase tracking-[0.3em] mx-auto py-4">
              Initialize New Audit
            </button>
          </div>
        )}
      </div>

      {!isUnlocked && (
        /* 
           UNLOCK OVERLAY POSITION:
           Placed at 100px from the top of the container.
           Since container is 700px tall and Chat is fixed to bottom, 
           this guarantees the required gap.
        */
        <div className="unlock-overlay" style={{ top: '100px', position: 'absolute' }}>
          <div className="bg-black/95 backdrop-blur-3xl px-10 py-6 rounded-full border border-white/20 flex items-center gap-6 animate-bounce shadow-2xl shadow-primary/60">
             <div className="bg-primary p-3 rounded-full shadow-[0_0_20px_rgba(130,109,236,0.9)]">
               <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
             </div>
             <span className="font-black text-white text-[13px] uppercase tracking-[0.2em]">Unlock results by filling out details below</span>
          </div>
        </div>
      )}

      <ChatUnlock isOpen={showSheet} onPreview={() => { setShowSheet(false); window.parent.postMessage({ type: 'scrollToTop' }, '*'); }} onComplete={handleUnlock} isSubmitting={isSubmitting} />
      
      {!isUnlocked && !showSheet && (
        <button 
          onClick={() => { setShowSheet(true); window.parent.postMessage({ type: 'scrollToTop' }, '*'); }}
          className="fixed bottom-10 left-1/2 -translate-x-1/2 bg-primary text-white font-black py-5 px-12 rounded-full shadow-2xl z-40 transition-all hover:scale-110 active:scale-95 flex items-center gap-3 uppercase text-xs tracking-[0.2em] glow-btn"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path></svg>
          Verification Required
        </button>
      )}
    </div>
  );
};

export default Results;